import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

/**
 * Global "Scheduled Maintenance" banner shown at the very top of every page.
 *
 * The maintenance window is configured in `environment.maintenance`
 * (`enabled`, `from`, `to` — all in JST). When enabled, the banner is shown to
 * every visitor until the `to` time passes, after which it hides itself.
 *
 * The from/to times are formatted in JST (+0900) and injected into the
 * translated message so it reads correctly in both English and Japanese.
 */
@Component({
  selector: 'app-maintenance-notification',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  providers: [DatePipe],
  templateUrl: './maintenance-notification.component.html',
  styleUrls: ['./maintenance-notification.component.css']
})
export class MaintenanceNotificationComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Whether the banner should currently be displayed. */
  showBanner = false;

  /** Pre-formatted JST window passed as params to the translated message. */
  maintenanceFromJst = '';
  maintenanceToJst = '';

  @ViewChild('banner') private bannerRef?: ElementRef<HTMLElement>;

  constructor(private datePipe: DatePipe, private renderer: Renderer2) {}

  ngOnInit(): void {
    const config = environment.maintenance;

    if (!config || !config.enabled || !config.from || !config.to) {
      return;
    }

    const from = this.parseJst(config.from);
    const to = this.parseJst(config.to);

    // Bail out if the dates are invalid to avoid showing a broken banner.
    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      return;
    }

    // Show to every visitor while maintenance is upcoming or ongoing; hide once
    // the window has ended.
    const now = new Date();
    this.showBanner = now.getTime() <= to.getTime();

    if (this.showBanner) {
      const format = 'yyyy/MM/dd HH:mm';
      const jst = '+0900';
      this.maintenanceFromJst = this.datePipe.transform(from, format, jst) ?? '';
      this.maintenanceToJst = this.datePipe.transform(to, format, jst) ?? '';
    }
  }

  ngAfterViewInit(): void {
    // The banner is fixed-positioned on top of every page, so reserve space for
    // it by pushing the page body down by exactly the banner's height. This
    // keeps the logo and page content fully visible below the banner.
    this.applyBodyOffset();
  }

  @HostListener('window:resize')
  onResize(): void {
    // The banner can wrap to a different height on narrow screens; re-measure.
    this.applyBodyOffset();
  }

  ngOnDestroy(): void {
    this.renderer.removeStyle(document.body, 'padding-top');
  }

  private applyBodyOffset(): void {
    if (!this.showBanner) {
      return;
    }
    // Defer until the banner has been laid out so its height is accurate.
    setTimeout(() => {
      const height = this.bannerRef?.nativeElement?.offsetHeight ?? 0;
      this.renderer.setStyle(document.body, 'padding-top', `${height}px`);
    });
  }

  /**
   * Parses a simple JST date string ('YYYY-MM-DD HH:mm') into a Date. The value
   * is interpreted as Japan Standard Time (+09:00) regardless of the visitor's
   * local timezone. Returns an invalid Date if the format does not match.
   */
  private parseJst(value: string): Date {
    const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/.exec(value.trim());
    if (!match) {
      return new Date(NaN);
    }
    const [, y, mo, d, h, mi] = match;
    return new Date(`${y}-${mo}-${d}T${h}:${mi}:00+09:00`);
  }
}
